import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Persist sidebar collapsed state in localStorage
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return localStorage.getItem('admin_sidebar_collapsed') === 'true';
    });

    const [isMobileOpen, setIsMobileOpen] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/enter');
        }
    }, [navigate, location.pathname]);

    const toggleCollapse = () => {
        setIsCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('admin_sidebar_collapsed', String(next));
            return next;
        });
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/enter');
    };

    const navItems = [
        {
            group: 'ANALYTICS',
            items: [
                { label: 'Dashboard', path: '/dashboard', icon: '📊', description: 'Overview & metrics' },
                { label: 'Registration Stats', path: '/registration-statistic', icon: '📈', description: 'User signup charts & directory' },
                { label: 'User Feedbacks', path: '/feedbacks', icon: '📬', description: 'User feedback & support' }
            ]
        },
        {
            group: 'CONTENT MANAGEMENT',
            items: [
                { label: 'Courses List', path: '/course-list', icon: '📚', description: 'Manage courses' },
                { label: 'Create Course', path: '/course', icon: '➕', description: 'Add new interactive course' },
                { label: 'Mates List', path: '/mates-list', icon: '🤖', description: 'AI practice mates' },
                { label: 'Chat List', path: '/chat-list', icon: '💬', description: 'AI conversation logs' },
                { label: 'My Collections', path: '/collections-list', icon: '📁', description: 'Card collections' },
                { label: 'Daily Phrases', path: '/daily-phrases-list', icon: '📝', description: 'Phrase repository' },
                { label: 'Sources Library', path: '/sources', icon: '🖼️', description: 'Media & resources' }
            ]
        }
    ];

    const token = localStorage.getItem('token');
    if (!token) return null;

    const sidebarWidth = isCollapsed ? '72px' : '260px';

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#ccdff5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {/* Mobile Backdrop */}
            {isMobileOpen && (
                <div
                    onClick={() => setIsMobileOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(2px)',
                        zIndex: 990
                    }}
                />
            )}

            {/* Sidebar Container */}
            <aside
                style={{
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: sidebarWidth,
                    background: '#2c4345',
                    color: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    zIndex: 1000,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '4px 0 20px rgba(0,0,0,0.15)',
                    overflowX: 'hidden'
                }}
            >
                {/* Top Section */}
                <div>
                    {/* Header Brand */}
                    <div style={{
                        height: '70px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isCollapsed ? 'center' : 'space-between',
                        padding: isCollapsed ? '0' : '0 20px',
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        {!isCollapsed && (
                            <div
                                onClick={() => navigate('/dashboard')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    cursor: 'pointer'
                                }}
                            >
                                <span style={{ fontSize: '1.5rem' }}>⚡</span>
                                <span style={{
                                    fontWeight: '800',
                                    fontSize: '1.2rem',
                                    letterSpacing: '0.5px',
                                    color: '#ffffff',
                                    whiteSpace: 'nowrap'
                                }}>
                                    AI Cards Admin
                                </span>
                            </div>
                        )}

                        {isCollapsed && (
                            <div
                                onClick={() => navigate('/dashboard')}
                                style={{ fontSize: '1.6rem', cursor: 'pointer' }}
                                title="AI Cards Admin Dashboard"
                            >
                                ⚡
                            </div>
                        )}

                        <button
                            onClick={toggleCollapse}
                            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                            style={{
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                color: '#ffffff',
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                display: isCollapsed ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        >
                            ◀
                        </button>
                    </div>

                    {/* Navigation Items */}
                    <div style={{ padding: isCollapsed ? '16px 8px' : '20px 14px' }}>
                        {navItems.map((group, groupIdx) => (
                            <div key={groupIdx} style={{ marginBottom: '20px' }}>
                                {!isCollapsed && (
                                    <div style={{
                                        fontSize: '0.7rem',
                                        fontWeight: '700',
                                        color: '#8da8aa',
                                        letterSpacing: '1px',
                                        marginBottom: '8px',
                                        paddingLeft: '12px',
                                        textTransform: 'uppercase'
                                    }}>
                                        {group.group}
                                    </div>
                                )}

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {group.items.map((item) => {
                                        const isActive = location.pathname === item.path ||
                                            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                                        return (
                                            <button
                                                key={item.path}
                                                onClick={() => {
                                                    navigate(item.path);
                                                    setIsMobileOpen(false);
                                                }}
                                                title={isCollapsed ? item.label : undefined}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '14px',
                                                    width: '100%',
                                                    padding: isCollapsed ? '12px 0' : '10px 14px',
                                                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                                                    background: isActive ? '#3c5a5d' : 'transparent',
                                                    color: isActive ? '#ffffff' : '#b0c4c6',
                                                    border: 'none',
                                                    borderRadius: '10px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: isActive ? '700' : '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    position: 'relative'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                                                        e.currentTarget.style.color = '#ffffff';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.background = 'transparent';
                                                        e.currentTarget.style.color = '#b0c4c6';
                                                    }
                                                }}
                                            >
                                                <span style={{ fontSize: '1.25rem', minWidth: '24px', textAlign: 'center' }}>
                                                    {item.icon}
                                                </span>

                                                {!isCollapsed && (
                                                    <span style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {item.label}
                                                    </span>
                                                )}

                                                {isActive && !isCollapsed && (
                                                    <span style={{
                                                        marginLeft: 'auto',
                                                        width: '6px',
                                                        height: '6px',
                                                        borderRadius: '50%',
                                                        background: '#5eead4'
                                                    }} />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer Section */}
                <div style={{
                    padding: isCollapsed ? '16px 8px' : '16px',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    background: '#233638'
                }}>
                    {!isCollapsed && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '12px',
                            paddingLeft: '4px'
                        }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: '#3c5a5d',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '700',
                                fontSize: '1rem',
                                color: '#ffffff'
                            }}>
                                A
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                                <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#ffffff' }}>Administrator</div>
                                <div style={{ fontSize: '0.75rem', color: '#8da8aa' }}>Logged In</div>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isCollapsed && (
                            <button
                                onClick={toggleCollapse}
                                title="Expand Sidebar"
                                style={{
                                    width: '100%',
                                    height: '38px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: 'none',
                                    color: '#ffffff',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                            >
                                ▶
                            </button>
                        )}

                        <button
                            onClick={handleLogout}
                            title="Sign Out"
                            style={{
                                width: '100%',
                                padding: isCollapsed ? '10px 0' : '10px 14px',
                                background: '#dc2626',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                        >
                            <span>🚪</span>
                            {!isCollapsed && <span>Logout</span>}
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <div style={{
                flex: 1,
                marginLeft: sidebarWidth,
                transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                minWidth: 0
            }}>
                {/* Floating Open Sidebar Button when Collapsed */}
                {isCollapsed && (
                    <button
                        onClick={toggleCollapse}
                        title="Expand Navigation Menu"
                        style={{
                            position: 'fixed',
                            top: '16px',
                            left: '84px',
                            zIndex: 900,
                            background: '#2c4345',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '8px 14px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#3c5a5d'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#2c4345'}
                    >
                        <span>▶ Sidebar</span>
                    </button>
                )}

                <Outlet />
            </div>
        </div>
    );
};

export default AdminLayout;
